<template>
  <div id="appointment-list-list-view" class="datatable-container">
    <vs-table ref="table" :data="appointments">
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between mb-4">
        <div class="flex flex-wrap-reverse  data-list-btn-container">
          <date-picker v-model="dateRange" range class="mr-2"></date-picker>
          <v-select v-model="statusField" :options="statusOptions" label="label" class="mr-2"></v-select>
          <v-select v-model="workflowStatusField" :options="workflowStatusOptions" label="label"></v-select>
        </div>

        <vs-input
          icon-no-border
          placeholder="Search"
          v-model="search"
          class="input-rounded-full"
          icon="icon-search"
          icon-pack="feather"
        />
      </div>

      <template slot="thead">
        <vs-th sort-key="advisorName">Advisor Name</vs-th>
        <vs-th sort-key="userName">User Name</vs-th>
        <vs-th sort-key="startDatetime">Start Date Time</vs-th>
        <vs-th sort-key="endDatetime">End Date Time</vs-th>
        <vs-th sort-key="status">Status</vs-th>
        <vs-th sort-key="createdAt">Created At</vs-th>
        <vs-th>Action</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody>
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.advisor.name }}</vs-td>

            <vs-td>{{ tr.user.username }}</vs-td>

            <vs-td>{{ tr.startDatetime | moment('DD MMM YYYY, hh:mm A') }}</vs-td>

            <vs-td>{{ tr.endDatetime | moment('DD MMM YYYY, hh:mm A') }}</vs-td>

            <vs-td>
              <vs-chip :color="getStatusColor(tr.status)">
                {{ getStatusLabel(tr.status) }}
              </vs-chip>
            </vs-td>

            <vs-td>{{ tr.createdAt | moment('DD MMM YYYY, hh:mm A') }}</vs-td>

            <vs-td class="whitespace-no-wrap">
              <!--prettier-ignore-->
              <feather-icon
                icon="EyeIcon"
                svgClasses="w-5 h-5 hover:text-primary stroke-current mr-2"
                @click.stop="handleCancelAppointment(tr)" />

              <feather-icon
                icon="EditIcon"
                svgClasses="w-5 h-5 hover:text-primary stroke-current mr-2"
                @click.stop="handleEdit(tr)" />

              <feather-icon
                v-if="isPendingPreAssessmentReview(tr) || isPendingPostAssessmentReview(tr)"
                icon="CheckSquareIcon"
                svgClasses="w-5 h-5 hover:text-primary stroke-current"
                @click.stop="handleShowAssessmentReviewPopup(tr)" />
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>

    <Pagination :total="totalPage" v-model="currentPage" />

    <vs-popup
      v-if="showPopup"
      :active.sync="showPopup"
      title="Order"
    >
      <span v-if="!popupContent">Not available</span>
      <span v-else>
        <vs-row v-for="(item, i) in orderDisplayColumns" :key="i" class="mb-2">
          <vs-col vs-w="5" class="font-medium">{{ item.label }}:</vs-col>
          <vs-col vs-w="7">
            <span v-if="item.isDate">{{ getKeyValueFromContent(item.key) | moment('DD MMM YYYY, hh:mm A') }}</span>
            <span v-else>{{ getKeyValueFromContent(item.key) || '-' }}</span>
          </vs-col>
        </vs-row>
      </span>
    </vs-popup>

    <vs-popup
      v-if="showAssessmentReviewPopup"
      :active.sync="showAssessmentReviewPopup"
      title="Assessment"
    >
      <span v-if="!popupContent">Not available</span>
      <span v-else>
        <div v-if="isPendingPreAssessmentReview(popupContent)">
          <b>Pre Assessment:</b>
          <a class="underline text-primary block" target="_blank" :href="popupContent.advisorReportFile.preAssessmentFile">
            {{ popupContent.advisorReportFile.preAssessmentFile }}
          </a>
        </div>

        <div v-if="isPendingPostAssessmentReview(popupContent)">
          <b>Post Assessment:</b>
          <a class="underline text-primary block" target="_blank" :href="popupContent.advisorReportFile.assessmentFile">
            {{ popupContent.advisorReportFile.assessmentFile }}
          </a>
        </div>

        <vs-button
          class="p-4 mt-4 float-right shadow-drop mr-4 rounded-lg cursor-pointer flex items-center justify-between text-lg font-medium"
          color="primary"
          @click="approveAssessment(popupContent)"
        >
          Approve
        </vs-button>
      </span>
    </vs-popup>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Pagination from '@/components/Pagination';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/en';
import _ from 'lodash';
import moment from 'moment';

export default {
  components: {
    Pagination,
    DatePicker,
  },
  data() {
    return {
      dateRange: [],
      status: '',
      workflowStatus: '',
      itemsPerPage: 10,
      currentPage: 1,
      pageTotal: 0,
      totalItem: 0,
      totalPage: 0,
      filter: {},
      filterQuery: '',
      showPopup: false,
      showAssessmentReviewPopup: false,
      popupContent: null,
      sorting: 'startDatetime,DESC',
      search: '',
    };
  },
  computed: {
    ...mapState({
      appointments: ({ appointment }) => appointment.appointments,
      updateAppointmentState: ({ appointment }) => appointment.apiState.updateAppointment,
    }),
    workflowStatusOptions() {
      return [
        { label: '-', value: '' },
        { label: 'PENDING REVIEW (PRE)', value: 'PENDING_REVIEW_PRE_ASSESSMENT' },
        { label: 'PENDING REVIEW (POST)', value: 'SUMMARY_SUBMITTED' },
      ];
    },
    workflowStatusField: {
      get() {
        const workflowStatus = this.workflowStatusOptions.find(x => x.value == this.workflowStatus);
        return workflowStatus ? { label: workflowStatus.label, value: this.workflowStatus } : null;
      },
      set(val) {
        this.workflowStatus = val.value;
      }
    },
    statusOptions() {
      return [
        { label: 'ALL', value: '' },
        { label: 'CONFIRMED', value: 'CONFIRMED' },
        { label: 'CANCELLED', value: 'CANCELLED' },
        { label: 'PENDING PAYMENT', value: 'PENDING_PAYMENT' },
      ];
    },
    statusField: {
      get() {
        const status = this.statusOptions.find(x => x.value == this.status);
        return status ? { label: status.label, value: this.status } : null;
      },
      set(val) {
        this.status = val.value;
      }
    },
    orderDisplayColumns() {
      return [
        { label: 'Amount', key: 'appointmentOrder.displayAmount', isDate: false },
        { label: 'Payment Method', key: 'appointmentOrder.paymentMethod', isDate: false },
        { label: 'Transaction ID', key: 'appointmentOrder.transactionId', isDate: false },
        { label: 'Advisor Name', key: 'advisor.name', isDate: false },
        { label: 'User Name', key: 'user.username', isDate: false },
        { label: 'Appointment Date Time', key: 'startDatetime', isDate: true },
        { label: 'Platform', key: 'appointmentOrder.platform', isDate: false },
        { label: 'Remarks', key: 'remarks', isDate: false },
        { label: 'Created At', key: 'appointmentOrder.createdAt', isDate: true },
      ]
    },
    getStatusLabel() {
      return value => _.find(this.statusOptions, { value }).label;
    },
    getWorkflowStatusLabel() {
      return value => _.find(this.workflowStatusOptions, { value }).label;
    },
    queryParams() {
      return {
        page: this.currentPage,
        limit: this.itemsPerPage,
        filter: [..._.values(this.filter)],
        sort: this.sorting
      };
    },
    getKeyValueFromContent() {
      return key => _.get(this.popupContent, key);
    },
    isPendingPreAssessmentReview() {
      return appointment =>
        appointment.workflowStatus == 'PENDING_REVIEW_PRE_ASSESSMENT' && appointment.advisorReportFile && appointment.advisorReportFile.preAssessmentFile;
    },
    isPendingPostAssessmentReview() {
      return appointment =>
        appointment.workflowStatus == 'SUMMARY_SUBMITTED' && appointment.advisorReportFile && appointment.advisorReportFile.assessmentFile;
    },
  },
  methods: {
    ...mapActions({
      _getAppointments: 'appointment/getAppointments',
      updateAppointment: 'appointment/updateAppointment',
    }),
    getStatusColor(status) {
      if (status === 'PENDING_PAYMENT') return 'warning';
      if (status === 'CANCELLED') return 'danger';
      if (status === 'CONFIRMED') return 'success';

      return 'primary';
    },
    getAppointments(params) {
      this._getAppointments(params)
        .then(response => {
          this.currentPage = parseInt(response.headers['x-pagination-page']);
          this.pageTotal = parseInt(response.headers['x-pagination-count']);
          this.totalItem = parseInt(response.headers['x-pagination-total']);
          this.totalPage = parseInt(response.headers['x-pagination-page-count']);
        })
        .catch(err => {
          console.error({ err });
        });
    },
    handleShowPopup(appointment) {
      this.showPopup = true;
      this.popupContent = appointment || null;
    },
    handleShowAssessmentReviewPopup(appointment) {
      this.showAssessmentReviewPopup = true;
      this.popupContent = appointment || null;
    },
    closePopup() {
      this.showPopup = false;
      this.popupContent = null;
    },
    handleCancelAppointment(appointment) {
      return this.$vs.dialog({
        color: 'danger',
        title: `Appointment Cancellation`,
        text: `Are you sure want to cancel?`,
        acceptText: 'Yes',
        accept: async () => {
          await this.updateAppointment({id: appointment.id, body: { status: 'CANCELLED' }});
          this.getAppointments(this.queryParams);
        },
      });
    },
    approveAssessment(appointment) {
      return this.$vs.dialog({
        color: 'success',
        title: `Assessment`,
        text: `Are you sure want to approve?`,
        acceptText: 'Yes',
        accept: async () => {
          const workflowStatus = appointment.workflowStatus === 'PENDING_REVIEW_PRE_ASSESSMENT' ? 'PENDING_CONSULTATION' : 'COMPLETED';

          await this.updateAppointment({id: appointment.id, body: { workflowStatus }});
          this.getAppointments(this.queryParams);
        },
      });
    },
    handleEdit(appointment) {
      this.$router.push({ name: 'appointment-detail', params: { id: appointment.id }});
    },
  },
  async mounted() {
    this.getAppointments({
      page: this.currentPage,
      limit: this.itemsPerPage,
      sort: this.sorting
    });
  },
  watch: {
    dateRange() {
      if(this.dateRange[0] && this.dateRange[1]) {
        const field = 'startDatetime';
        const end = moment(this.dateRange[1]).format('YYYY-MM-DD 23:59:59');

        this.$set(this.filter, 'dateRangeStart', `${field}||$gte||${this.dateRange[0].toISOString()}}`);
        this.$set(this.filter, 'dateRangeEnd', `${field}||$lte||${moment(end).toISOString()}`);
      } else {
        this.$delete(this.filter, 'dateRangeStart');
        this.$delete(this.filter, 'dateRangeEnd');
      }

      return this.getAppointments(this.queryParams);
    },
    status() {
      if(this.status) {
        this.$set(this.filter, 'status', `status||$eq||${this.status}`);
      } else {
        this.$delete(this.filter, 'status');
      }

      return this.getAppointments(this.queryParams);
    },
    workflowStatus() {
      if(this.workflowStatus) {
        this.$set(this.filter, 'workflowStatus', `workflowStatus||$eq||${this.workflowStatus}`);
      } else {
        this.$delete(this.filter, 'workflowStatus');
      }

      return this.getAppointments(this.queryParams);
    },
    async currentPage(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getAppointments(this.queryParams);
      }
    },
    search: _.debounce(async function(newValue, oldValue) {
      if (newValue !== oldValue) {
        let params = this.queryParams;
        if (newValue) {
          params = {
            ...params,
            page: 1, // back to first page upon searching
            or: [`advisor.name||$eq||${this.search}`, `user.username||$eq||${this.search}`],
          };
        }
        this.getAppointments(params);
      }
    }, 1000),
    'updateAppointmentState.success'(value) {
      if (value) {
        this.$vs.notify({
          title: 'Successful update!',
          color: 'success',
          position: 'top-center',
        });
        this.showAssessmentReviewPopup = false;
      }
    },
    'updateAppointmentState.error'(error) {
      if (error) {
        this.$vs.notify({
          title: 'Error!',
          text: error.data.message,
          color: 'danger',
          position: 'top-center',
        });
      }
    },
  },
};
</script>
<style lang="scss">
#appointment-list-list-view {
  .mx-input {
    height: auto;
    font-size: 1rem;
    line-height: 1.5;
    letter-spacing: 0.01rem;
    padding: 8px;
  }
}
.con-vs-popup {
  z-index: 52003;
}
.v-select .vs__dropdown-toggle {
  padding: 6px 0 8px;
  min-width: 240px;
  background: white;
}
</style>
